import { useState, useEffect } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material"
import { Loader2, Search, UserPlus, Mail, Phone } from "lucide-react"
import { customersApi } from "@crm-src/lib/customers-api"
import type { Customer } from "@crm-src/types/customer"

interface LinkCustomerToResellerModalProps {
  isOpen: boolean
  onClose: () => void
  resellerId: string
  onSuccess: () => void
  excludeCustomerIds?: string[] // IDs of customers already associated with this reseller
}

export function LinkCustomerToResellerModal({
  isOpen,
  onClose,
  resellerId,
  onSuccess,
  excludeCustomerIds = []
}: LinkCustomerToResellerModalProps) {
  const { toast } = useToast()
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<Set<string>>(new Set())
  const [isLinking, setIsLinking] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadCustomers()
      setSelectedCustomerIds(new Set())
      setSearchTerm("")
    }
  }, [isOpen])

  const loadCustomers = async () => {
    setLoading(true)
    try {
      // Get all customers (not leads) that are not already associated with this reseller
      const response = await customersApi.getAll({
        customer_type: 'Customer',
        per_page: 1000 // Get a large number to show all available
      })

      const allCustomers = response.customers || []
      // Filter out customers already associated with this reseller
      const availableCustomers = allCustomers.filter(
        (customer: Customer) =>
          !excludeCustomerIds.includes(customer.id) &&
          customer.reseller?.id?.toString() !== resellerId
      )

      setCustomers(availableCustomers)
    } catch (error) {
      console.error('Error loading customers:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load customers"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleToggleCustomer = (customerId: string) => {
    setSelectedCustomerIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(customerId)) {
        newSet.delete(customerId)
      } else {
        newSet.add(customerId)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    const filteredCustomers = getFilteredCustomers()
    if (selectedCustomerIds.size === filteredCustomers.length) {
      setSelectedCustomerIds(new Set())
    } else {
      setSelectedCustomerIds(new Set(filteredCustomers.map(c => c.id)))
    }
  }

  const handleLink = async () => {
    if (selectedCustomerIds.size === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select at least one customer"
      })
      return
    }

    setIsLinking(true)
    try {
      await customersApi.bulkAssignReseller(
        Array.from(selectedCustomerIds),
        resellerId
      )

      toast({
        title: "Success",
        description: `Successfully linked ${selectedCustomerIds.size} customer(s) to this reseller`
      })

      setSelectedCustomerIds(new Set())
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error linking customers:', error)
      toast({
        variant: "destructive",
        title: "Error",
        description: error.response?.data?.errors?.[0] || "Failed to link customers"
      })
    } finally {
      setIsLinking(false)
    }
  }

  const getFilteredCustomers = () => {
    if (!searchTerm) return customers

    const term = searchTerm.toLowerCase()
    return customers.filter(customer =>
      customer.company_name?.toLowerCase().includes(term) ||
      customer.full_name?.toLowerCase().includes(term) ||
      customer.email?.toLowerCase().includes(term)
    )
  }

  const filteredCustomers = getFilteredCustomers()

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { maxHeight: "80vh" } }}>
      <DialogTitle>Link Customers to Reseller</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <DialogContentText sx={{ mb: 2 }}>
          Select existing customers to associate with this reseller
        </DialogContentText>

        <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search customers by name, company, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} />
                </InputAdornment>
              ),
            }}
          />

          {/* Select All */}
          {filteredCustomers.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1, borderBottom: 1, borderColor: "divider" }}>
              <Checkbox
                size="small"
                sx={{ p: 0.5 }}
                checked={selectedCustomerIds.size === filteredCustomers.length && filteredCustomers.length > 0}
                onChange={handleSelectAll}
              />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Select all ({filteredCustomers.length} customers)
              </Typography>
            </Box>
          )}

          {/* Customer List */}
          <Box sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 1 }}>
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                <Box sx={{ color: "text.disabled", display: "flex", animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } }}>
                  <Loader2 size={24} />
                </Box>
              </Box>
            ) : filteredCustomers.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: "text.disabled" }}>
                <Typography variant="body2">
                  {searchTerm ? "No customers found matching your search" : "No available customers to link"}
                </Typography>
              </Box>
            ) : (
              filteredCustomers.map((customer) => (
                <Box
                  key={customer.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={(e) => {
                    // Don't toggle if clicking directly on the checkbox (it handles its own click)
                    if ((e.target as HTMLElement).closest('[role="checkbox"]')) {
                      return
                    }
                    handleToggleCustomer(customer.id)
                  }}
                >
                  <Box onClick={(e) => e.stopPropagation()} sx={{ display: "flex" }}>
                    <Checkbox
                      size="small"
                      sx={{ p: 0.5 }}
                      checked={selectedCustomerIds.has(customer.id)}
                      onChange={() => handleToggleCustomer(customer.id)}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ color: "success.main", flexShrink: 0, display: "flex" }}>
                        <UserPlus size={16} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {customer.company_name || customer.full_name}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5, fontSize: "0.75rem", color: "text.disabled" }}>
                      {customer.email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Mail size={12} />
                          <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{customer.email}</Box>
                        </Box>
                      )}
                      {customer.phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Phone size={12} />
                          <Box component="span">{customer.phone}</Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={isLinking}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={isLinking || selectedCustomerIds.size === 0}
          {...(isLinking ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}
        >
          {isLinking
            ? "Linking..."
            : `Link ${selectedCustomerIds.size > 0 ? `(${selectedCustomerIds.size})` : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
